import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { TopCustomerBiDto } from "../../../shared/dtos/bi/TopCustomerBiDto";

interface TopB2BCustomersWidgetProps {
  data?: TopCustomerBiDto[];
}

const TopB2BCustomersWidget: React.FC<TopB2BCustomersWidgetProps> = ({
  data = [],
}) => {
  const theme = useTheme();

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Top B2B Customers
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(value)
                  }
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                />
                <YAxis
                  type="category"
                  dataKey="customerName"
                  width={120}
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  tickFormatter={(val) =>
                    val.length > 15 ? `${val.substring(0, 15)}...` : val
                  }
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                  contentStyle={{ borderRadius: 8 }}
                  formatter={(value, _name, props) => [
                    formatCurrency(Number(value)),
                    `Invoiced Amount (${props.payload.invoiceCount} invoices)`,
                  ]}
                />
                <Bar
                  dataKey="totalInvoicedAmount"
                  fill={theme.palette.secondary.main}
                  barSize={20}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 300,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No B2B customer records found.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TopB2BCustomersWidget;
